import { useEffect, useMemo, useState } from 'react';
import PdfPreviewModal from '@/components/feature/PdfPreviewModal';
import { tableDocument } from '@/utils/pdfDocument';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/base/Button';
import { API_MODE, api, apiList } from '@/utils/api';
import { formatDateTime, formatMoney } from '@/utils/format';
import { channelPosition, ledgerSummary, needsAttention } from '@/utils/ledger';
import type { Transaction, TransactionOrigin } from '@/types/portal';
import LedgerToolbar from './components/LedgerToolbar';
import LedgerSummary from './components/LedgerSummary';
import ChannelPositionCard from './components/ChannelPositionCard';
import TransactionTable from './components/TransactionTable';
import { originMeta } from './components/originMeta';
import TransactionDetailModal from './components/TransactionDetailModal';
import NeedsAttentionPanel from './components/NeedsAttentionPanel';

const RECON_OPTIONS = ['All', 'Reconciled', 'Pending', 'Unmatched', 'Exception'] as const;
const FEE_OPTIONS = ['Exclude fees', 'Include fees'] as const;

const messageOf = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

/** Every ledger line: the API pages its list, the screen filters and pages locally. */
async function loadAll(): Promise<Transaction[]> {
  const rows: Transaction[] = [];
  for (let page = 1; page <= 50; page += 1) {
    const { data, meta } = await apiList<Transaction>(`/transactions?per_page=100&page=${page}`);
    rows.push(...data);
    if (page >= (meta?.total_pages ?? 1) || data.length === 0) break;
  }
  return rows;
}

/** Type tabs mirror the flows that create ledger lines. */
const TYPE_TABS: { id: 'All' | TransactionOrigin; label: string }[] = [
  { id: 'All', label: 'All types' },
  { id: 'single', label: 'Single payments' },
  { id: 'bulk', label: 'Bulk batches' },
  { id: 'topup', label: 'Wallet top-ups' },
  { id: 'fee', label: 'Rail fees' },
  { id: 'reversal', label: 'Reversals' },
];

const PAGE_SIZES = [10, 25, 50];

/** The screen that creates a line, for the "open source" action. */
function sourceRoute(row: Transaction): string {
  switch (row.origin) {
    case 'bulk':
      return '/payments/bulk';
    case 'topup':
      return '/payments/wallets';
    case 'fee':
      return '/payouts';
    default:
      return '/payments/send';
  }
}

export default function Transactions() {
  const navigate = useNavigate();
  const [type, setType] = useState<'All' | TransactionOrigin>('All');
  const [wallet, setWallet] = useState('All wallets');
  const [recon, setRecon] = useState('All');
  const [fees, setFees] = useState('Exclude fees');
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [notice, setNotice] = useState('');
  const [syncedAt, setSyncedAt] = useState('');
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = async () => {
    setError('');
    try {
      const rows = await loadAll();
      setAllTransactions(rows);
      return rows;
    } catch (loadError) {
      setError(messageOf(loadError, 'Could not load the transactions.'));
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const walletOptions = useMemo(
    () => ['All wallets', ...Array.from(new Set(allTransactions.map((row) => row.wallet))).sort()],
    [allTransactions],
  );

  const typeTabs = useMemo(
    () =>
      TYPE_TABS.map((tab) => ({
        id: tab.id,
        label: tab.label,
        count:
          tab.id === 'All'
            ? allTransactions.length
            : allTransactions.filter((row) => row.origin === tab.id).length,
      })),
    [allTransactions],
  );

  const filtered = useMemo(
    () =>
      allTransactions.filter((row) => {
        if (type !== 'All' && row.origin !== type) return false;
        if (fees === 'Exclude fees' && row.origin === 'fee') return false;
        if (wallet !== 'All wallets' && row.wallet !== wallet) return false;
        if (recon !== 'All' && row.recon !== recon) return false;
        const day = row.createdAt.slice(0, 10);
        if (from && day < from) return false;
        if (to && day > to) return false;
        if (query.trim()) {
          const text = query.toLowerCase();
          const haystack = [row.reference, row.party, row.description, row.campaign, row.relatedRef]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(text)) return false;
        }
        return true;
      }),
    [allTransactions, type, fees, wallet, recon, from, to, query],
  );

  const activeCount = [
    type !== 'All',
    fees !== 'Exclude fees',
    wallet !== 'All wallets',
    recon !== 'All',
    from !== '',
    to !== '',
    query.trim() !== '',
  ].filter(Boolean).length;

  // Any filter change starts the view back at page one.
  useEffect(() => {
    setPage(1);
  }, [type, fees, wallet, recon, from, to, query, pageSize]);

  const summary = useMemo(() => ledgerSummary(filtered), [filtered]);
  const positions = useMemo(() => channelPosition(filtered), [filtered]);
  const currencies = useMemo(
    () => Array.from(new Set(filtered.map((row) => row.currency))).sort(),
    [filtered],
  );
  const attention = useMemo(() => filtered.filter(needsAttention), [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const paged = filtered.slice(start, start + pageSize);

  const resetFilters = () => {
    setType('All');
    setWallet('All wallets');
    setRecon('All');
    setFees('Exclude fees');
    setQuery('');
    setFrom('');
    setTo('');
  };

  const openSource = (row: Transaction) => {
    setSelected(null);
    navigate(sourceRoute(row));
  };

  const openInvoice = (invoiceNumber: string) => {
    setSelected(null);
    navigate(`/invoices/inv-${invoiceNumber.replace('INV-', '')}`);
  };

  const sync = async () => {
    const rows = await refresh();
    if (!rows) return;
    const at = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    setSyncedAt(at);
    setNotice(`Ledger synced at ${at}. ${rows.length} line${rows.length === 1 ? '' : 's'} loaded.`);
  };

  const reconcile = async () => {
    setError('');
    try {
      const updated = await api<Transaction[]>('/transactions/reconcile', {
        method: 'POST',
        body: JSON.stringify({ transactionIds: attention.map((row) => row.id) }),
      });
      const byId = new Map(updated.map((row) => [row.id, row]));
      setAllTransactions((rows) => rows.map((row) => byId.get(row.id) ?? row));
      setNotice(`Reconciled ${updated.length} line${updated.length === 1 ? '' : 's'}.`);
    } catch (reconcileError) {
      setError(messageOf(reconcileError, 'Could not reconcile.'));
    }
  };

  // The ledger lines in view, shared by the CSV and the PDF.
  const exportTable = () => {
    const header = [
      'Date',
      'Type',
      'Recipient',
      'Party type',
      'Description',
      'Amount',
      'Currency',
      'Direction',
      'Wallet',
      'Rail',
      'Status',
      'Reconciled',
      'Reference',
    ];
    const rows = filtered.map((row) =>
      [
        formatDateTime(row.createdAt),
        originMeta[row.origin].label,
        row.party,
        row.partyType,
        row.description,
        String(row.amount),
        row.currency,
        row.direction,
        row.wallet,
        row.rail,
        row.status,
        row.recon,
        row.reference,
      ],
    );
    return { header, rows };
  };

  const [pdfHtml, setPdfHtml] = useState('');
  const exportPdf = () => {
    // Fewer, wider columns than the CSV so the PDF stays readable.
    const header = ['Date', 'Type', 'Recipient', 'Description', 'Amount', 'Status', 'Reference'];
    const rows = filtered.map((row) => [
      formatDateTime(row.createdAt),
      originMeta[row.origin].label,
      row.party,
      row.description,
      `${row.direction === 'Debit' ? '−' : '+'}${formatMoney(row.amount, row.currency)}`,
      row.status,
      row.reference,
    ]);
    setPdfHtml(
      tableDocument(
        {
          title: `Transactions ${new Date().toISOString().slice(0, 10)}`,
          meta: activeCount > 0 ? [`${activeCount} filter${activeCount === 1 ? '' : 's'} applied`] : ['All ledger lines'],
        },
        header,
        rows,
      ),
    );
  };

  const exportCsv = () => {
    const { header, rows } = exportTable();
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((line) => line.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'twiva-transactions.csv';
    link.click();
    URL.revokeObjectURL(url);
    setNotice(`Exported ${filtered.length} ledger line${filtered.length === 1 ? '' : 's'} to CSV.`);
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Transactions
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            {syncedAt
              ? `Last synced ${syncedAt} — every credit and debit across the ledger.`
              : 'Every credit and debit across the ledger — run Sync to refresh.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon="ri-refresh-line" onClick={() => void sync()}>
            Sync
          </Button>
          <Button icon="ri-add-line" onClick={() => navigate('/payments/send')}>
            New payment
          </Button>
        </div>
      </div>

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 transition-colors hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="mt-5 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5"
        >
          <span className="inline-flex items-center gap-2 text-sm text-primary-800">
            <i className="ri-error-warning-line" />
            {error}
          </span>
          <button
            type="button"
            onClick={() => setError('')}
            className="cursor-pointer text-primary-700 transition-colors hover:text-primary-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      <div className="mt-6" data-print-hide>
        <LedgerToolbar
          types={typeTabs}
          type={type}
          onType={(id) => setType(id as 'All' | TransactionOrigin)}
          wallets={walletOptions}
          wallet={wallet}
          onWallet={setWallet}
          reconOptions={RECON_OPTIONS}
          recon={recon}
          onRecon={setRecon}
          feeOptions={FEE_OPTIONS}
          fees={fees}
          onFees={setFees}
          query={query}
          onQuery={setQuery}
          from={from}
          onFrom={setFrom}
          to={to}
          onTo={setTo}
          activeCount={activeCount}
          onReset={resetFilters}
        />
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-2" data-print-hide>
        <Button variant="outline" size="sm" icon="ri-file-excel-2-line" onClick={exportCsv}>
          Export Excel
        </Button>
        <Button variant="outline" size="sm" icon="ri-file-pdf-2-line" onClick={exportPdf}>
          Export PDF
        </Button>
      </div>

      <PdfPreviewModal open={pdfHtml !== ''} onClose={() => setPdfHtml('')} title="Transactions" html={pdfHtml} />

      <div className="mt-4">
        <LedgerSummary summary={summary} />
      </div>

      <div className="mt-4">
        <ChannelPositionCard positions={positions} currencies={currencies} />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-foreground-500">
          Showing {filtered.length === 0 ? 0 : start + 1}–{start + paged.length} of {filtered.length}{' '}
          transaction{filtered.length === 1 ? '' : 's'}
        </p>
        <label className="flex items-center gap-2 text-xs text-foreground-500">
          Rows per page
          <select
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
            className="h-8 cursor-pointer rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-700 focus:border-primary-400 focus:outline-none"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-12 text-center text-sm text-foreground-500">
            Loading transactions…
          </div>
        ) : (
          <TransactionTable
            rows={paged}
            startIndex={start + 1}
            onSelect={setSelected}
            onOpenSource={openSource}
          />
        )}
      </div>

      {totalPages > 1 ? (
        <div className="mt-4 flex items-center justify-center gap-2" data-print-hide>
          <button
            type="button"
            onClick={() => setPage((value) => Math.max(1, value - 1))}
            disabled={currentPage === 1}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Previous page"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <span className="text-xs text-foreground-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
            disabled={currentPage === totalPages}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next page"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      ) : null}

      <div className="mt-6" data-print-hide>
        <NeedsAttentionPanel
          rows={attention}
          onSelect={setSelected}
          onReconcile={() => void reconcile()}
          reconciliationAvailable={API_MODE !== 'agency'}
        />
      </div>

      <TransactionDetailModal
        open={selected !== null}
        row={selected}
        onClose={() => setSelected(null)}
        onOpenSource={openSource}
        onOpenInvoice={openInvoice}
      />
    </div>
  );
}